import React, { useState, useEffect, useRef } from "react";
import CompanySelect from "../component/Common/CountrySelect";
import Footer from "../component/Footer/FooterCommon";
import EmployeeCard from "../component/EmployeeCard/EmployeeCard";
import Navbar from "../component/Navbar/NavbarCommon";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { API_URL } from "../utils/config";
import { toast } from "react-toastify";
import { ResponseTypes } from "../api/types";
import FilterPopup from "../component/FilterPopup";
import { useAccountStore } from "../store/useAccountStore";

const EmployeeDirectory = () => {
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [employeesData, setEmployeesData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({ department: "", location: "", role: "" });
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { user } = useAccountStore();

  // Navbar show/hide on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDiff = Math.abs(currentScrollY - lastScrollY);
      if (scrollDiff < 5) return;

      if (currentScrollY < lastScrollY - 100 || currentScrollY < 30) {
        setIsNavbarVisible(true);
      } else if (currentScrollY > lastScrollY + 5 && currentScrollY > 50) {
        setIsNavbarVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  // Infinite Scroll Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 1.0 }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading]);

  // Fetch employees
  useEffect(() => {
    fetchAllEmployees();
  }, [searchQuery, page, filters, user?.country]);

  const fetchAllEmployees = async () => {
    try {
      setLoading(true);
      const countryName = user?.country === 'IN' ? 'India' : user?.country === 'BD' ? 'Bangladesh' : user?.country === 'LK' ? 'Sri Lanka' : 'India';
      const query = new URLSearchParams({
        search: searchQuery,
        page: String(page),
        limit: "20",
        country: countryName,
        ...(filters.department && { department: filters.department }),
        ...(filters.location && { location: filters.location }),
        ...(filters.role && { role: filters.role }),
      });

      const { data, error } = await api.get<ResponseTypes>(`${API_URL.users}?${query.toString()}`);
      if (error) {
        toast.error(error.message);
        setLoading(false);
        return;
      }

      const users = (data as any)?.data?.users || [];
      if (page === 1) setEmployeesData(users);
      else setEmployeesData((prev) => [...prev, ...users]);

      const total = (data as any)?.data?.total || 0;
      setHasMore(employeesData.length + users.length < total);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      {showFilter && (
        <FilterPopup
          filters={filters}
          setFilters={(f) => {
            setFilters(f);
            setPage(1);
            setShowFilter(false);
          }}
          onClose={() => setShowFilter(false)}
        />
      )}

      {/* Navbar */}
      <Navbar isVisible={isNavbarVisible} />

      {/* Fixed Header */}
      <div
        className="fixed left-0 right-0 bg-white z-40 md:mt-6"
        style={{
          top: isNavbarVisible ? "100px" : "0px",
          transition: "top 0.3s ease-in-out",
        }}
      >
        <div className="px-3 sm:px-4 lg:px-10 lg:py-2">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-0">
            <CompanySelect country={user?.country} />
            <p className="font-semibold text-deepTeal text-lg sm:text-xl lg:text-2xl text-center sm:text-right">
              Employee Directory
            </p>
          </div>

          {/* Search + Filter */}
          <div className="hidden sm:flex items-center w-full mt-3">
            <button
              onClick={() => navigate("/")}
              className="flex items-center justify-center px-3 py-2.5 shadow-[0px_0px_2.86px_0px_#00000040] rounded-[10px] h-12 mr-3"
            >
              <img src="assets/clarity_arrow-line.svg" alt="Back" />
            </button>
            <div className="flex-1 mr-4 lg:mr-12">
              <div className="flex items-center shadow-[0px_0px_2.86px_0px_#00000040] px-4 py-2.5 border rounded-lg bg-white gap-3">
                <img src="assets/prime_search.svg" alt="Search" className="w-5 h-5" />
                <input
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search name, role or email"
                  className="w-full text-gray-700 focus:outline-none bg-transparent text-lg"
                />
              </div>
            </div>
            <button
              onClick={() => setShowFilter(true)}
              className="flex items-center gap-2 bg-darkgreen text-white text-lg px-4 py-3 rounded-[10px] shadow-[0px_0px_2.86px_0px_#00000040]"
            >
              <img src="assets/filter-icon.svg" alt="Filter" className="w-5 h-5" />
              <span>Filter</span>
            </button>
          </div>
        </div>
      </div>

      {/* Employee Cards Section */}
      <div
        className="flex-1 relative"
        style={{
          marginTop: isNavbarVisible ? "250px" : "180px",
          transition: "margin-top 0.3s ease-in-out",
        }}
      >
        <div className="h-full overflow-y-auto relative">
          <div
            className="fixed left-0 right-0 bg-white z-30 pointer-events-none"
            style={{
              top: 0,
              height: isNavbarVisible ? "260px" : "180px",
              transition: "height 0.3s ease-in-out",
            }}
          />
          <div className="px-3 sm:px-4 lg:px-10 relative pt-8 pb-20">
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 w-full">
              {employeesData.map((employee) => (
                <EmployeeCard key={employee._id} employee={employee} />
              ))}
            </div>

            {/* Loader */}
            <div ref={loaderRef} className="flex justify-center py-6">
              {loading ? (
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <svg
                    className="animate-spin h-5 w-5 text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    ></path>
                  </svg>
                  Loading more employees...
                </div>
              ) : !hasMore ? (
                <p className="text-gray-400 text-sm mt-4">
                  {/* No Employee Found */}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white z-50 shadow-lg">
        <Footer />
      </div>
    </div>
  );
};

export default EmployeeDirectory;
